import type { AlgoliaProduct } from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia'
import type { DateTime } from '@backmarket/http-api/src/standards/DateTime'

export type MessageContent = string
// How a user message originated, for tracking:
// - 'typed': the user wrote it in the input
// - 'suggested': the user tapped a prompt suggestion
// - 'seeded': auto-sent by a deep-link seeded launch (not authored in-session)
export type MessageInputMethod = 'typed' | 'suggested' | 'seeded'

export const EVENT_ROLE_USER = 'user' as const
export type EventRequestUserRole = typeof EVENT_ROLE_USER

export const EVENT_ROLE_ASSISTANT = 'assistant' as const
export type EventRequestAssistantRole = typeof EVENT_ROLE_ASSISTANT

export interface MessageEvent {
  type: 'message'
  role: EventRequestUserRole | EventRequestAssistantRole
  content: MessageContent
}

export type PromptSuggestion = string

export interface PromptSuggestionEvent {
  type: 'prompt_suggestion'
  role: EventRequestAssistantRole
  prompts: PromptSuggestion[]
}

export interface CategorizedPromptSuggestionEvent {
  type: 'categorized_prompt_suggestion'
  role: EventRequestAssistantRole
  categories: Array<{
    title: string
    icon: string
    prompts: PromptSuggestion[]
  }>
}

export interface ProductRecommendationEvent {
  type: 'product_recommendation'
  role: EventRequestAssistantRole
  products: AlgoliaProduct[]
}

export interface NoProductRecommendationEvent {
  type: 'no_product_recommendation'
  role: EventRequestAssistantRole
  content: MessageContent
}

export type PageContextType = 'product_page'

export type ChatRequestPageContext = {
  type: PageContextType
  product_id: string
}

// Live page context pushed while the web sidebar is open, distinct from
// ChatRequestPageContext (seed-only, sent once at chat creation). structured_data
// is the raw schema.org JSON-LD objects found on the page, deduped by @type;
// empty when the page has none.
export interface BrowsingContextRequestBody {
  url: string
  structured_data: object[]
}

export interface ChatRequestBody {
  context: string
  by: 'tester' | 'user'
  resume_latest?: boolean
  page_context?: ChatRequestPageContext
}

export type ChatEvent =
  | MessageEvent
  | PromptSuggestionEvent
  | CategorizedPromptSuggestionEvent
  | ProductRecommendationEvent
  | NoProductRecommendationEvent

export interface ChatResponse {
  id: string
  events: ChatEvent[]
}

// Message is written by the user
export const MESSAGE_ROLE_USER = '20' as const
export type MessageRequestUserRole = typeof MESSAGE_ROLE_USER

// Message is based on a prompt suggestion
export const MESSAGE_ROLE_SUGGESTION = '0' as const
export type MessageRequestSuggestionRole = typeof MESSAGE_ROLE_SUGGESTION

export interface MessageRequestBody {
  content: string
  role: MessageRequestUserRole | MessageRequestSuggestionRole
  page_context?: ChatRequestPageContext
}

export interface MessageResponse {
  events: ChatEvent[]
}

export interface ChatCreatedEvent {
  type: 'chat_created'
  id: string
}

export interface TextDeltaEvent {
  type: 'text_delta'
  content: string
}

export type StreamChunk = TextDeltaEvent | ChatEvent
export type StreamingEvent = ChatCreatedEvent | StreamChunk

export type ChatHistory = {
  chatId: string
  title: string
  createdAt: DateTime
  updatedAt: DateTime
}
